import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { CustomSegmentedOptionObject } from '../../components';
import { useQueryParams } from '../../queryParams/useQueryParams';

export interface ContentSegmentOption<Segment extends string = string>
  extends CustomSegmentedOptionObject<Segment> {
  children?: ReactNode;
}

export interface ContentSegmentsProviderProps<Segment extends string = string>
  extends PropsWithChildren {
  segments: ContentSegmentOption<Segment>[];
  onChangeSegment?: (value: Segment) => void;
}

export const ContentSegmentsProvider = <Segment extends string = string>(
  props: ContentSegmentsProviderProps<Segment>
) => {
  const { onChangeSegment, segments, children } = props;
  const [queryParams, setQueryParams] = useQueryParams();
  const currentSegment = queryParams.segment || segments[0].value;
  const segmentRef = useRef(currentSegment);
  const setCurrentSegment = useCallback(
    (segment: string) => {
      setQueryParams({ segment });
    },
    [setQueryParams]
  );
  useEffect(() => {
    // Trigger segment change on initial load (if segment loaded from URL)
    if (segmentRef.current) {
      onChangeSegment?.(currentSegment as Segment);
    }
  }, [currentSegment, onChangeSegment]);

  return (
    <ContentSegmentsContext.Provider
      value={{ currentSegment, setCurrentSegment }}
    >
      {children}
    </ContentSegmentsContext.Provider>
  );
};

export const useCurrentSegment = <Segment extends string = string>() => {
  const { currentSegment, setCurrentSegment } = useContext(
    ContentSegmentsContext
  ) as unknown as ContentSegmentsContextValue<Segment>;

  if (typeof currentSegment === 'undefined' || setCurrentSegment === noop) {
    throw new Error(
      'ContentSegmentsContext must be consumed within ContentSegments'
    );
  }

  return { currentSegment, setCurrentSegment };
};

interface ContentSegmentsContextValue<Segment extends string = string> {
  currentSegment: Segment;
  setCurrentSegment: (value: Segment) => void;
}

/* eslint-disable @typescript-eslint/no-empty-function */
const noop = () => {};

const ContentSegmentsContext = createContext<ContentSegmentsContextValue>({
  currentSegment: undefined!,
  setCurrentSegment: noop,
});
